import React, { useEffect, useState } from 'react'
import { SkeletonForm } from '@node-space/storybook-components/dist/SkeletonLoader'
import { Stepper } from '@node-space/storybook-components/dist/Stepper'
import { Box } from '@node-space/ui-components'
import { useVerificationContext } from 'hooks/context/useVerificationContext'
import { FormGetError } from 'pages/Verification/_components/FormGetError'
import { DynamicQuestionsMapping, DynamicQuestionsStep } from '../helpers/types'
import { useActiveStep } from '../helpers/useActiveStep'
import { useRequiredSteps } from '../helpers/useRequiredSteps'
import { useUnsavedChanges } from '../helpers/useUnsavedChanges'
import { BusinessComplianceStep } from './BusinessComplianceStep'

export const BusinessComplianceForm = () => {
  const {
    dynamicQuestions,
    setUnsavedChanges: setHasUnsavedChanges,
    submitStep,
  } = useVerificationContext()
  const { getRequiredSteps } = useRequiredSteps(dynamicQuestions?.data)

  const [requiredSteps, setRequiredSteps] = useState<DynamicQuestionsMapping>()

  const requiredStepKeys = !!requiredSteps ? Object.keys(requiredSteps) : []

  const { activeStepKey, isLoadingActiveStep, onBack, goToNextStep } = useActiveStep(
    requiredSteps,
    requiredStepKeys
  )

  const { unsavedChanges, saveUnsavedChanges, removeUnsavedChanges } =
    useUnsavedChanges(activeStepKey)

  const hasUnsavedChanges = !!Object?.keys(unsavedChanges)?.length

  const handleContinueLastStep = () => {
    dynamicQuestions?.refetch()
    submitStep()
  }

  const onContinue = (updatedQuestionsMapping: DynamicQuestionsStep) => {
    if (unsavedChanges?.[activeStepKey]) {
      removeUnsavedChanges()
    }
    if (activeStepKey === requiredStepKeys?.at(-1)) {
      handleContinueLastStep()
      return
    }
    if (updatedQuestionsMapping) {
      setRequiredSteps(currentSteps => {
        const updatedSteps = { ...currentSteps }
        updatedSteps[activeStepKey] = updatedQuestionsMapping
        return updatedSteps
      })
    }
    goToNextStep()
  }

  useEffect(() => {
    !dynamicQuestions?.isFetching && setRequiredSteps(getRequiredSteps())
  }, [dynamicQuestions?.isFetching])

  useEffect(() => {
    setHasUnsavedChanges(hasUnsavedChanges)
  }, [hasUnsavedChanges])

  return isLoadingActiveStep ? (
    <SkeletonForm name="Business compliance" />
  ) : dynamicQuestions?.isError ? (
    <FormGetError />
  ) : (
    <Box paddingT={24}>
      <Stepper<string> stepKeys={requiredStepKeys} activeStepKey={activeStepKey} onBack={onBack} />
      <BusinessComplianceStep
        questions={requiredSteps?.[activeStepKey]}
        unsavedChanges={unsavedChanges?.[activeStepKey]}
        saveUnsavedChanges={saveUnsavedChanges}
        onContinue={onContinue}
      />
    </Box>
  )
}
